import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { message } from "antd";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Sheet, DetailRow, Field,
  TextInput, SelectInput, Pill, Mono, Ico, fmt, useMobileMenu,
} from "../produits/productMobileKit";
import { auditDate, listAuditConnections, parseAuditDetails } from "./auditMobileApi";
import useAuditScope from "../../gestion-audit/useAuditScope";
import {
  createAuditRequestGate,
  isAuditRequestCanceled,
} from "../../gestion-audit/auditRequestGate";

const PAGE = 30;
const actions = [{ key: "all", label: "Toutes" }, { key: "LOGIN", label: "Connexions" }, { key: "LOGOUT", label: "Déconnexions" }, { key: "LOGIN_FAILURE", label: "Échecs" }];
const actionMeta = (value, status) => {
  if (value === "LOGIN_FAILURE" || status === "failure") return ["Échec", "danger", "log-in"];
  if (value === "LOGOUT") return ["Déconnexion", "off", "log-out"];
  return ["Connexion", "ok", "log-in"];
};

export default function AuditConnectionsMobile() {
  const onMenu = useMobileMenu();
  const {
    auditScope,
    companyId,
    isSuperAdmin,
    scope: effectiveAuditScope,
    scopeKey: auditScopeKey,
    setAuditScope,
  } = useAuditScope();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [actionType, setActionType] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({ ipAddress: "", startDate: "", endDate: "" });
  const latestAuditScopeKeyRef = useRef(auditScopeKey);
  useLayoutEffect(() => { latestAuditScopeKeyRef.current = auditScopeKey; }, [auditScopeKey]);
  const previousAuditScopeKeyRef = useRef(auditScopeKey);
  const requestGateRef = useRef(null);
  if (!requestGateRef.current) requestGateRef.current = createAuditRequestGate();

  useEffect(() => { const timer = window.setTimeout(() => setDebouncedQuery(query), 350); return () => window.clearTimeout(timer); }, [query]);
  const load = useCallback(async (nextOffset = 0, append = false) => {
    const request = requestGateRef.current.start(auditScopeKey);
    setLoading(true);
    try {
      const data = await listAuditConnections(
        { limit: PAGE, offset: nextOffset, userSearch: debouncedQuery, actionType, ...filters, startDate: filters.startDate ? `${filters.startDate} 00:00:00` : "", endDate: filters.endDate ? `${filters.endDate} 23:59:59` : "" },
        companyId,
        { scope: effectiveAuditScope, signal: request.signal }
      );
      if (!request.isCurrent(latestAuditScopeKeyRef.current)) return;
      const nextRows = data.events || [];
      setRows((previous) => append ? [...previous, ...nextRows] : nextRows);
      setTotal(Number(data.total || 0)); setOffset(nextOffset); setHasMore(Boolean(data.hasMore));
    } catch (error) {
      if (isAuditRequestCanceled(error, request.signal) || !request.isCurrent(latestAuditScopeKeyRef.current)) return;
      message.error(error.message || "Impossible de charger l'historique des connexions");
      if (!append) setRows([]);
    } finally {
      const isCurrent = request.isCurrent(latestAuditScopeKeyRef.current);
      request.finish();
      if (isCurrent) setLoading(false);
    }
  }, [actionType, auditScopeKey, companyId, debouncedQuery, effectiveAuditScope, filters]);
  useEffect(() => {
    const scopeChanged = previousAuditScopeKeyRef.current !== auditScopeKey;
    previousAuditScopeKeyRef.current = auditScopeKey;
    if (scopeChanged) {
      requestGateRef.current.cancel();
      setRows([]);
      setTotal(0);
      setDetail(null);
    }
    setOffset(0);
    setHasMore(false);
    load(0, false);
  }, [auditScopeKey, load]);
  useEffect(() => () => requestGateRef.current?.cancel(), []);

  const counts = useMemo(() => ({ login: rows.filter((row) => row.action_type === "LOGIN").length, failure: rows.filter((row) => row.action_type === "LOGIN_FAILURE" || row.status === "failure").length }), [rows]);
  const reset = () => { setFilters({ ipAddress: "", startDate: "", endDate: "" }); setActionType(""); };

  return <ProductScreen>
    <Appbar title="Connexions" subtitle={`${fmt(total)} événement${total > 1 ? "s" : ""}`} onMenu={onMenu} action={<button className="iconbtn" aria-label="Filtres" onClick={() => setFiltersOpen(true)}><Ico name="sliders-horizontal" /></button>} />
    <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}><Summary items={[{ label: "Chargés", value: fmt(rows.length) }, { label: "Connexions", value: fmt(counts.login) }, { label: "Échecs", value: fmt(counts.failure) }]} /><SearchBar value={query} onChange={setQuery} placeholder="Nom, email ou ID…" /><Chips value={actionType || "all"} onChange={(value) => setActionType(value === "all" ? "" : value)} options={actions} /></div>
    <div className="body">{loading && !rows.length ? <EmptyState loading title="Chargement des connexions…" /> : !rows.length ? <EmptyState icon="log-in" title="Aucune connexion trouvée" /> : <div className="list">{rows.map((row) => { const meta = actionMeta(row.action_type, row.status); return <button className="rcard" key={row.id} onClick={() => setDetail(row)}><Mono name={row.username || row.user_email || "U"} square /><div className="rcard__main"><div className="rcard__name">{row.username || row.user_email || "Utilisateur inconnu"}</div><div className="rcard__meta"><Pill tone={meta[1]} label={meta[0]} /><span className="rcard__sub">{row.ip_address || "IP inconnue"} · {auditDate(row.created_at)}</span></div></div><span className="rcard__chev"><Ico name="chevron-right" /></span></button>; })}{hasMore ? <button className="btn btn--ghost" onClick={() => load(offset + PAGE, true)} disabled={loading}>Charger plus ({rows.length}/{total})</button> : null}</div>}</div>

    <Sheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filtres de connexion">{isSuperAdmin ? <Field label="Périmètre"><SelectInput value={auditScope} onChange={(scope) => setAuditScope(scope || "company")} options={[{ value: "company", label: "Entreprise sélectionnée" }, { value: "all", label: "Toutes les entreprises" }]} /></Field> : null}<Field label="Adresse IP"><TextInput value={filters.ipAddress} onChange={(ipAddress) => setFilters((previous) => ({ ...previous, ipAddress }))} placeholder="192.168.1.10" /></Field><Field label="Date début"><div className="ctrl"><input type="date" value={filters.startDate} onChange={(event) => setFilters((previous) => ({ ...previous, startDate: event.target.value }))} /></div></Field><Field label="Date fin"><div className="ctrl"><input type="date" value={filters.endDate} onChange={(event) => setFilters((previous) => ({ ...previous, endDate: event.target.value }))} /></div></Field><div className="sheet__actions"><button className="btn btn--ghost" onClick={reset}>Réinitialiser</button><button className="btn btn--primary" onClick={() => setFiltersOpen(false)}>Appliquer</button></div></Sheet>

    <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.username || detail?.user_email || "Connexion"} subtitle={detail ? auditDate(detail.created_at, true) : ""}>{detail ? <><DetailRow label="Événement" value={actionMeta(detail.action_type, detail.status)[0]} /><DetailRow label="Statut" value={detail.status === "failure" ? "Échec" : "Succès"} /><DetailRow label="Email" value={detail.user_email || parseAuditDetails(detail.details).email || "—"} /><DetailRow label="Entreprise" value={detail.company_name || (detail.company_id ? `#${detail.company_id}` : "—")} /><DetailRow label="Adresse IP" value={detail.ip_address || "—"} /><DetailRow label="Navigateur" value={detail.user_agent || "—"} />{Object.keys(parseAuditDetails(detail.details)).length ? <pre style={{ marginTop: 10, padding: 12, borderRadius: 12, background: "var(--canvas)", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>{JSON.stringify(parseAuditDetails(detail.details), null, 2)}</pre> : null}</> : null}</Sheet>
  </ProductScreen>;
}
